import type { RecurringItem } from '@/api/recurring'
import { MarkDoneControl } from '@/components/recurring/MarkDoneControl'
import { useRecurringActions } from '@/components/recurring/useRecurringActions'
import { dueBadge } from '@/components/recurring/dueLabel'
import { Badge } from '@/components/ui/badge'
import { useFormatCurrency } from '@/hooks/useFormatCurrency'
import { useIsReadOnly } from '@/hooks/useIsReadOnly'
import { CategoryIcon } from '@/lib/categoryIcon'
import { approxAmount, getDefaultRecurringIcon } from '@/lib/recurring'
import { formatShortDate } from '@/lib/formatDate'
import { cn } from '@/lib/utils'

/** One reminder on one of its due dates: name, due badge, account, amount and Mark paid. */
export function ReminderRow({
  item,
  date,
  today,
}: {
  item: RecurringItem
  /** The due date being shown — a weekly reminder appears on several. */
  date: string
  today: string
}) {
  const { format } = useFormatCurrency()
  const isReadOnly = useIsReadOnly()
  const actions = useRecurringActions(item)
  const [y, m, d] = today.split('-').map(Number)
  const badge = dueBadge(date, new Date(y, m - 1, d))
  const income = item.type === 'INCOME'
  return (
    <li className="flex items-center gap-2.5 border-t border-[var(--fl-divider)] py-2 text-[13.5px] first:border-t-0">
      <span className="flex h-[30px] w-[30px] shrink-0 items-center justify-center rounded-[9px] bg-[var(--fl-hero-bg)] text-[var(--fl-hero-fg)]">
        <CategoryIcon
          icon={item.icon ?? getDefaultRecurringIcon(item.kind ?? 'OTHER', item.categoryIcon)}
          size={14}
        />
      </span>
      <span className="min-w-0 flex-1">
        <span className="block truncate text-[var(--fl-text-1)]">{item.name}</span>
        <span className="flex min-w-0 items-center gap-1.5 text-xs text-[var(--fl-text-2)]">
          {badge && badge.tone !== 'primary' && <Badge tone={badge.tone}>{badge.text}</Badge>}
          {item.financialAccountName && (
            <span className="truncate">{item.financialAccountName}</span>
          )}
        </span>
      </span>
      <span
        className={cn('shrink-0 font-semibold tabular-nums', income && 'text-[var(--fl-income)]')}
      >
        {income && '+'}
        {approxAmount(item, format(item.amount))}
      </span>
      {/* Paying settles the reminder's current due date, so later dates can't be paid ahead. */}
      {date === item.nextDueDate ? (
        !isReadOnly && (
          <MarkDoneControl item={item} actions={actions} label={income ? 'Got it' : 'Mark paid'} />
        )
      ) : (
        <span className="shrink-0 text-xs text-[var(--fl-text-3)]">
          after {formatShortDate(item.nextDueDate)}
        </span>
      )}
      {actions.dialogs}
    </li>
  )
}
